import {
    Box,
    Divider,
    ListItemText,
    Card,
    Dialog,
    IconButton,
    Tooltip,
    ListItemIcon,
    Menu,
    MenuItem,
    Typography,
    useMediaQuery,
    Tab,
    Tabs,
} from "@mui/material";
import type { IRanking, IRecipe, ISavedRecipes } from "../types/recipe";
import { useCallback, useState, useEffect, memo, useMemo } from "react";
import type { RootState } from "../store";
import { useSelector } from "react-redux";
import ImageUpload from "./ImageUpload";
import axiosService from "../utils/axios";
import { AddAPhoto, Close, Collections, Favorite, FavoriteBorder } from "@mui/icons-material";
import Theme from "../utils/theme";
import { setRecipeInfo, visibilityOptions, visibilityLabels, getRankingAverage } from "../utils/helpers/recipeHelper";
import type { IRecipeVisibility } from "../types/recipe";
import Recipe from "./Recipe";
import ImageCardFeed from "./ImageCardFeed";
import { scrollbarCss } from "../utils/themeScrollbar";
import RecipeComments from "./RecipeComments";
import type { IComment } from "../types/comment";
import { updateCommentLikes } from "../utils/helpers/commentHelper";
import { StarRating } from "./StarRating";

interface IRecipeProps {
    disableAllButtons?: boolean;
    recipeData: ISavedRecipes;
    setRecipeData: React.Dispatch<React.SetStateAction<ISavedRecipes>>;
    containerHeight?: string;
}

export type IRecipeSetComment = (type: "newComment" | "newLike", newComments?: IComment[], commentId?: string) => void;

const MemoizedImageCardFeed = memo(ImageCardFeed);

const SavedRecipe: React.FC<IRecipeProps> = ({ disableAllButtons = false, recipeData, setRecipeData, containerHeight = "" }) => {
    const authState = useSelector((state: RootState) => state.auth);
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);
    const isGuest = !Boolean(authState.account);

    const rankingValue = recipeData.ranking
        ? recipeData.ranking.filter((item: IRanking) => {
              return item.user.username === authState.account?.username;
          })
        : [];

    const [visibilityLoading, setVisiblityLoading] = useState(false);
    const [openAddImageModal, setOpenAddImageModal] = useState(false);
    const [openImageModal, setOpenImageModal] = useState(false);
    const [recipeAvatarUrl, setRecipeAvatarUrl] = useState("");
    const [recipeVisibility, setRecipeVisibility] = useState<IRecipeVisibility>(recipeData.visibility ?? "personal");
    const [visibilityAnchorEl, setVisibilityAnchorEl] = useState(null);
    const [updateKey, setUpdateKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(0);
    const [rating, setRating] = useState(authState.account?.username && rankingValue.length > 0 ? rankingValue[0].value : 0);

    const smUp = useMediaQuery(theme.breakpoints.up("md"));

    useEffect(() => {
        if (visibilityLoading) {
            // TODO: use visibilityLoading for something
        }
        if (recipeData.image) setRecipeAvatarUrl(recipeData.image.url);
    }, [recipeData, visibilityLoading]);

    const isUser = authState.account && recipeData.user ? authState.account.username === recipeData.user.username : false;

    const uploadRecipeImage = useCallback(
        (previewUrl: string | null, data: { file: File | null }) => {
            if (!data.file) return;
            setLoading(true);
            const formData = new FormData();
            formData.append("recipeId", recipeData._id);
            formData.append("image", data.file);

            axiosService
                .post(`${process.env.REACT_APP_API_URL}/recipes/image/upload`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((res) => {
                    setRecipeData((prev: ISavedRecipes) => {
                        return { ...prev, images: [...(prev.images ?? []), res.data.body] };
                    });
                    setOpenAddImageModal(false);
                })
                .finally(() => {
                    setUpdateKey((prev: number) => prev + 1);
                    setLoading(false);
                });
        },
        [recipeData._id, setRecipeData]
    );

    const handleOpenImageModal = () => {
        setOpenImageModal(true);
    };

    const current = visibilityOptions[recipeVisibility];
    const open = Boolean(visibilityAnchorEl);
    const CurrentIcon = current.Icon;
    const changeVisibility = useCallback(
        (newValue: IRecipeVisibility) => {
            setVisiblityLoading(true);

            axiosService
                .post(`${process.env.REACT_APP_API_URL}/recipes/update/visibility`, {
                    visibility: newValue,
                    recipeId: recipeData._id,
                })
                .then((res) => {
                    recipeData.visibility = res.data.body.visibility;
                    setRecipeVisibility(newValue);
                })
                .finally(() => {
                    setVisiblityLoading(false);
                });
        },
        [recipeData]
    );

    const handleOpenVisibility = (e: any) => setVisibilityAnchorEl(e.currentTarget);
    const handleCloseVisibility = () => setVisibilityAnchorEl(null);
    const handleSelectVisibility = (newValue: IRecipeVisibility) => {
        if (newValue !== recipeData.visibility) changeVisibility(newValue);
        handleCloseVisibility();
    };

    const handleRecipeLike = useCallback(() => {
        axiosService.post(`${process.env.REACT_APP_API_URL}/recipes/update/likes`, { recipeId: recipeData._id }).then((res) => {
            setRecipeData(setRecipeInfo(res));
        });
    }, [recipeData._id, setRecipeData]);

    const isFavorited = useCallback(() => {
        if (isGuest || !recipeData.likes) return;

        return recipeData.likes.some((like) => {
            return like.user.username === authState.account!.username;
        });
    }, [recipeData, isGuest, authState]);

    const setRecipeDataComments = useCallback(
        (type: "newComment" | "newLike", newComments?: IComment[], commentId?: string) => {
            if (!authState.account?.username) return;

            if (type === "newComment" && newComments) {
                setRecipeData((prev) => {
                    return { ...prev, comments: newComments };
                });
            } else if (commentId) {
                setRecipeData((prev) => {
                    return {
                        ...prev,
                        comments: updateCommentLikes(prev.comments, authState.account?.username!, commentId),
                    };
                });
            }
        },

        [authState.account?.username, setRecipeData]
    );

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                overflow: "auto",
                flexDirection: "column",
                width: "100%",
                ...scrollbarCss,
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", top: 0, right: 5, zIndex: 1 }}>
                {recipeAvatarUrl !== "" && (
                    <Card
                        sx={{
                            height: "250px",
                            width: "100%",
                            overflow: "hidden",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            component="img"
                            src={recipeAvatarUrl}
                            sx={{
                                width: "calc(100% - 100px)",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </Card>
                )}

                <Box sx={{ display: "flex" }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        aria-label="Followers tabs"
                        variant="fullWidth"
                        sx={{
                            width: "100%",
                            px: { xs: 1, sm: 2 },
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: "flex" }}>
                                    <Typography>Recipe</Typography>
                                </Box>
                            }
                            id="tab-pending"
                            aria-controls="panel-pending"
                        />
                        <Tab
                            label={
                                <Box sx={{ display: "flex" }}>
                                    <Typography>Comments</Typography>
                                </Box>
                            }
                            id="tab-approved"
                            aria-controls="panel-approved"
                        />
                    </Tabs>
                </Box>

                {tab === 0 && (
                    <Box
                        sx={{
                            mt: 1,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "center",
                            gap: 2,
                            px: 1,
                        }}
                    >
                        {!isGuest && (
                            <StarRating
                                average={getRankingAverage(recipeData.ranking)}
                                hoverable={true}
                                objectId={recipeData._id}
                                count={recipeData.ranking ? recipeData.ranking.length : 0}
                                value={rating}
                                onChange={setRating}
                            />
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                border: "2px solid " + theme.palette.primary.main,
                                borderRadius: 5,
                            }}
                        >
                            <Tooltip title="Images" arrow>
                                <IconButton onClick={handleOpenImageModal}>
                                    <Collections sx={{ color: theme.palette.primary.main }} />
                                </IconButton>
                            </Tooltip>

                            {!isGuest && !disableAllButtons && (
                                <>
                                    <Tooltip title="Like Recipe" arrow>
                                        <IconButton onClick={handleRecipeLike}>
                                            {isFavorited() ? <Favorite sx={{ color: theme.palette.primary.main }} /> : <FavoriteBorder />}
                                        </IconButton>
                                    </Tooltip>

                                    {isUser && (
                                        <>
                                            <Tooltip title={current.label} arrow>
                                                <IconButton
                                                    size="medium"
                                                    disabled={false}
                                                    onClick={handleOpenVisibility}
                                                    aria-label="Change visibility"
                                                    aria-haspopup="menu"
                                                    aria-expanded={open ? "true" : undefined}
                                                    sx={{
                                                        bgcolor: open ? "action.selected" : "transparent",
                                                        borderRadius: 2,
                                                        transition: (t) =>
                                                            t.transitions.create(["background-color", "transform"], {
                                                                duration: t.transitions.duration.shorter,
                                                            }),
                                                        "&:hover": { bgcolor: "action.hover", transform: "translateY(-1px)" },
                                                    }}
                                                >
                                                    <CurrentIcon sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                )}

                <Menu
                    anchorEl={visibilityAnchorEl}
                    open={open}
                    onClose={handleCloseVisibility}
                    elevation={8}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: 200,
                                borderRadius: 2,
                                overflow: "hidden",
                                boxShadow: (t) => t.shadows[8],
                            },
                        },
                    }}
                >
                    <Box sx={{ px: 2, py: 1.25, fontWeight: 600, opacity: 0.8 }}>Visibility</Box>
                    <Divider />
                    {Object.values(visibilityOptions).map(({ value, label, Icon }) => (
                        <MenuItem
                            key={value}
                            selected={recipeData.visibility ? recipeData.visibility === value : value === "personal"}
                            onClick={() => handleSelectVisibility(value)}
                            sx={{
                                py: 1.25,
                                "&.Mui-selected": { bgcolor: "action.selected" },
                                "& .MuiListItemText-primary": { fontWeight: 500 },
                            }}
                        >
                            <ListItemIcon>
                                <Icon sx={{ color: theme.palette.primary.main }} fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={label} secondary={visibilityLabels[value as keyof typeof visibilityLabels]} />
                        </MenuItem>
                    ))}
                </Menu>
            </Box>

            {tab === 1 ? (
                <RecipeComments refId={recipeData._id} comments={recipeData.comments} setComments={setRecipeDataComments} />
            ) : (
                <Recipe recipe={recipeData as IRecipe} savedRecipe={recipeData as ISavedRecipes} disableAllButtons={disableAllButtons} />
            )}

            {(openImageModal || openAddImageModal) && (
                <>
                    <Dialog
                        fullWidth
                        open={openImageModal}
                        onClose={() => setOpenImageModal(false)}
                        PaperProps={{
                            sx: {
                                height: "80dvh",
                                maxHeight: "80dvh",
                                width: "100%",
                                maxWidth: 1200,
                                m: 0,
                                py: 3,
                                px: 4,
                                borderRadius: 5,
                                display: "flex",
                                flexDirection: "column",
                            },
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "baseline", position: "relative" }}>
                            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                                <Typography variant={smUp ? "h4" : "h6"}>Images</Typography>
                                {isUser && (
                                    <Box
                                        component="button"
                                        sx={{
                                            display: "flex",
                                            borderRadius: 5,
                                            cursor: "pointer",
                                            alignItems: "center",
                                            pl: 1,
                                            height: 35,
                                            borderColor: theme.palette.primary.main,
                                            backgroundColor: theme.palette.primary.main,
                                            color: "white",
                                        }}
                                    >
                                        <Typography>Upload</Typography>
                                        <IconButton sx={{ color: "white" }} onClick={() => setOpenAddImageModal(true)}>
                                            <AddAPhoto />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>

                            <IconButton
                                sx={{ position: "absolute", right: -20, top: -15 }}
                                onClick={() => {
                                    setOpenImageModal(false);
                                    setOpenAddImageModal(false);
                                }}
                            >
                                <Close sx={{ fontSize: "32px" }} />
                            </IconButton>
                        </Box>
                        <Divider sx={{ width: "100%", my: 2 }} />
                        <Box sx={{ overflow: "auto", ...scrollbarCss }}>
                            <MemoizedImageCardFeed
                                recipeId={recipeData._id}
                                type="recipeId"
                                username={recipeData.user?.username}
                                setRecipeAvatarUrl={setRecipeAvatarUrl}
                                key={updateKey}
                            />
                        </Box>
                    </Dialog>

                    {isUser && (
                        <Dialog open={openAddImageModal} onClose={() => setOpenAddImageModal(false)}>
                            <Box sx={{ px: 2, py: 1 }}>
                                <Typography>Please choose an image to upload!</Typography>
                                <ImageUpload loading={loading} type="Recipe" handleImageAdd={uploadRecipeImage} />
                            </Box>
                        </Dialog>
                    )}
                </>
            )}
        </Box>
    );
};

export default SavedRecipe;
